import { clamp01 } from '../core/math.js';

export interface BeatDetectorOptions {
  /** How many recent flux values form the rolling baseline (~0.7 s at 60 fps). */
  readonly historySize: number;
  /** Flux must exceed the baseline mean multiplied by this factor to count as a beat. */
  readonly sensitivity: number;
  /** Minimum number of frames between two reported beats. */
  readonly cooldownFrames: number;
  /** Fraction of the spectrum (from the low end) used for onset detection. */
  readonly spectrumFraction: number;
  /**
   * Absolute noise gate: flux below this never counts as a beat, no matter how
   * quiet the baseline is. Without it, near-silence makes the relative threshold
   * (baseline × sensitivity) collapse towards zero and analyser noise fires
   * constant full-strength beats.
   */
  readonly minimumFlux: number;
}

export interface BeatResult {
  readonly isBeat: boolean;
  /** Normalised beat strength; 0 when `isBeat` is false. */
  readonly intensity: number;
}

export const DEFAULT_BEAT_OPTIONS: BeatDetectorOptions = {
  historySize: 43,
  sensitivity: 1.4,
  cooldownFrames: 9,
  spectrumFraction: 0.5,
  minimumFlux: 0.015,
};

const NO_BEAT: BeatResult = { isBeat: false, intensity: 0 };

/**
 * Spectral-flux onset detector.
 *
 * Each frame it sums the *increases* in bin magnitude since the previous frame
 * (energy rising fast across the low spectrum is what a beat sounds like),
 * then compares that flux against a rolling average. Pure and deterministic:
 * no clocks, no Web Audio — feed it spectra, it returns verdicts. Fully unit-testable.
 */
export class BeatDetector {
  private readonly options: BeatDetectorOptions;
  private previous: Float64Array | null = null;
  private readonly fluxHistory: number[] = [];
  private cooldown = 0;

  constructor(options: BeatDetectorOptions = DEFAULT_BEAT_OPTIONS) {
    this.options = options;
  }

  public update(spectrum: Readonly<Uint8Array>): BeatResult {
    const usedBins = Math.max(1, Math.floor(spectrum.length * this.options.spectrumFraction));
    const flux = this.computeFlux(spectrum, usedBins);
    if (flux === null) {
      return NO_BEAT;
    }

    this.record(flux);
    if (this.cooldown > 0) {
      this.cooldown -= 1;
      return NO_BEAT;
    }

    const baseline = this.averageFlux();

    const threshold = Math.max(baseline * this.options.sensitivity, this.options.minimumFlux);
    if (baseline <= 0 || flux <= threshold) {
      return NO_BEAT;
    }

    this.cooldown = this.options.cooldownFrames;
    const intensity = clamp01((flux - threshold) / threshold);
    return { isBeat: true, intensity };
  }

  /** Positive spectral flux vs the previous frame, or null on the very first frame. */
  private computeFlux(spectrum: Readonly<Uint8Array>, usedBins: number): number | null {
    const current = new Float64Array(usedBins);
    for (let i = 0; i < usedBins; i += 1) {
      current[i] = (spectrum[i] ?? 0) / 255;
    }

    const prev = this.previous;
    this.previous = current;
    if (prev?.length !== usedBins) {
      return null;
    }

    let flux = 0;
    for (let i = 0; i < usedBins; i += 1) {
      const rise = (current[i] ?? 0) - (prev[i] ?? 0);
      if (rise > 0) {
        flux += rise;
      }
    }
    return flux / usedBins;
  }

  /** Recorded every frame, including cooldown frames, to keep the baseline window honest. */
  private record(flux: number): void {
    this.fluxHistory.push(flux);
    if (this.fluxHistory.length > this.options.historySize) {
      this.fluxHistory.shift();
    }
  }

  private averageFlux(): number {
    let sum = 0;
    for (const value of this.fluxHistory) {
      sum += value;
    }
    return sum / this.fluxHistory.length;
  }
}
